import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calendar, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog.jsx';
import { Button } from '../ui/button.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Textarea } from '../ui/textarea';
import { formatDateWithTZ } from '../../lib/utils';

export const BookingModal = ({
  showBooking,
  setShowBooking,
  bookingBarbershop,
  setBookingBarbershop,
  loadClientHistory,
  loadUpcomingBookings,
  setSuccess,
  loading,
  setLoading,
  BACKEND_URL
}) => {
  const [bookingData, setBookingData] = useState({
    service: '',
    date: '',
    time: '',
    notes: ''
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isHighDemand, setIsHighDemand] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [calendarUrl, setCalendarUrl] = useState('');
  const [bookingComplete, setBookingComplete] = useState(false);

  const timeSlots = [];
  for (let hour = 8; hour < 21; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(timeString);
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Selecciona una fecha';
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return formatDateWithTZ(date, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Selecciona una hora';
    const [hours, minutes] = timeString.split(':');
    return `${hours}:${minutes}`;
  };

  const handleBooking = async () => {
    if (!bookingData.service || !bookingData.date || !bookingData.time) {
      setBookingError('Por favor completa todos los campos obligatorios.');
      return;
    }

    if (bookedSlots.includes(bookingData.time)) {
      setBookingError('Ese horario ya está reservado. Elige otro horario disponible.');
      return;
    }

    try {
      setLoading(true);
      const selectedService = bookingBarbershop?.services?.find(s => s.name === bookingData.service);

      const response = await axios.post(`${BACKEND_URL}/api/bookings`, {
        barbershop_id: bookingBarbershop.id,
        barber_id: bookingBarbershop.barber_id,
        service: bookingData.service,
        date: new Date(`${bookingData.date}T${bookingData.time}`).toISOString(),
        price: selectedService?.price || 0,
        notes: bookingData.notes
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const createdCalendarUrl = response?.data?.calendar_url || '';
      if (createdCalendarUrl) {
        setCalendarUrl(createdCalendarUrl);
        const link = document.createElement('a');
        link.href = createdCalendarUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setBookingComplete(true);
      }
      if (!createdCalendarUrl) {
        setShowBooking(false);
        setBookingBarbershop(null);
        setBookingData({ service: '', date: '', time: '', notes: '' });
        setDateOpen(false);
        setTimeOpen(false);
      }
      setSuccess('Reserva enviada correctamente al barbero');

      loadClientHistory();
      loadUpcomingBookings();
    } catch (error) {
      console.error('Booking error:', error);
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || 'Error al enviar la reserva';
      if (status === 409) {
        setBookingError('Ese horario ya está reservado. Elige otro horario disponible.');
      } else {
        setBookingError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!showBooking) {
      setBookingData({ service: '', date: '', time: '', notes: '' });
      setDateOpen(false);
      setTimeOpen(false);
      setBookedSlots([]);
      setIsHighDemand(false);
      setBookingError('');
      setCalendarUrl('');
      setBookingComplete(false);
    }
  }, [showBooking]);

  useEffect(() => {
    const loadBookedSlots = async () => {
      if (!bookingBarbershop?.id || !bookingData.date) {
        setBookedSlots([]);
        setIsHighDemand(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BACKEND_URL}/api/bookings/barbershop/${bookingBarbershop.id}/date`,
          {
            params: { date: bookingData.date },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setBookedSlots(response.data.booked_slots || []);
        setIsHighDemand(Boolean(response.data.is_high_demand));
      } catch (error) {
        console.error('Error loading booked slots:', error);
        setBookedSlots([]);
        setIsHighDemand(false);
      }
    };

    if (showBooking) {
      loadBookedSlots();
    }
  }, [bookingBarbershop?.id, bookingData.date, showBooking, BACKEND_URL]);

  return (
    <Dialog open={showBooking} onOpenChange={(open) => {
      setShowBooking(open);
      if (!open) {
        setBookingData({ service: '', date: '', time: '', notes: '' });
        setDateOpen(false);
        setTimeOpen(false);
      }
    }}>
      <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Reservar en {bookingBarbershop?.name}</DialogTitle>
          <DialogDescription className="sr-only">
            Selecciona servicio, fecha y hora para enviar la reserva.
          </DialogDescription>
        </DialogHeader>
        {isHighDemand && (
          <div className="bg-amber-500/10 border border-amber-500/60 text-amber-200 text-xs rounded-lg px-3 py-2">
            Alta demanda en esta barbería. Algunos horarios ya están ocupados.
          </div>
        )}
        {bookingComplete && calendarUrl && (
          <div className="rounded-lg border border-amber-500/60 bg-amber-500/10 px-3 py-3 text-xs text-amber-200 space-y-2">
            <div className="font-semibold text-amber-300">Reserva creada ✅</div>
            <div>Si no se abrió automáticamente, agrega el evento a tu calendario aquí:</div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                className="border-amber-400 text-amber-300 hover:bg-amber-400 hover:text-black"
                onClick={() => window.open(calendarUrl, '_blank', 'noopener,noreferrer')}
              >
                Agregar a calendario
              </Button>
              <Button
                type="button"
                variant="outline"
                className="border-gray-700 text-gray-200"
                onClick={() => {
                  setShowBooking(false);
                  setBookingBarbershop(null);
                  setBookingData({ service: '', date: '', time: '', notes: '' });
                  setDateOpen(false);
                  setTimeOpen(false);
                  setCalendarUrl('');
                  setBookingComplete(false);
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        )}
        {bookingBarbershop?.accepting_bookings === false && (
          <div className="bg-red-500/10 border border-red-500/60 text-red-200 text-xs rounded-lg px-3 py-2">
            Esta barbería no está aceptando reservas en este momento.
          </div>
        )}
        {!bookingComplete && (
          <div className="space-y-4">
          <div>
            <label className="text-white text-sm font-medium mb-2 block">Servicio *</label>
            <Select
              value={bookingData.service}
              onValueChange={(value) => setBookingData({ ...bookingData, service: value })}
            >
              <SelectTrigger className="bg-gray-800 border-gray-700 text-amber-400 hover:bg-gray-700">
                <SelectValue placeholder="Selecciona un servicio" />
              </SelectTrigger>
              <SelectContent>
                {bookingBarbershop?.services?.map((service, index) => (
                  <SelectItem
                    key={index}
                    value={service.name}
                    className="text-amber-400 hover:bg-gray-800 focus:bg-gray-800 focus:text-amber-300"
                  >
                    {service.name} - ${service.price?.toLocaleString()} ({service.duration}min)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {bookingError && (
            <div className="rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {bookingError}
            </div>
          )}

          <div>
            <label className="text-white text-sm font-medium mb-2 block">Fecha *</label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-gray-800 border-gray-700 text-amber-400 hover:bg-gray-700"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {formatDate(bookingData.date)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-black border-gray-700" align="start">
                <CalendarComponent
                  mode="single"
                  selected={bookingData.date ? new Date(bookingData.date) : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateString = `${year}-${month}-${day}`;
                      setBookingData({ ...bookingData, date: dateString });
                      setDateOpen(false);
                    }
                  }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  className="rounded-md border-0"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-sm font-medium text-amber-400",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-amber-400",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-amber-400 rounded-md w-8 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                    day: "h-8 w-8 p-0 font-normal text-white hover:bg-amber-500/20 hover:text-amber-400",
                    day_selected: "bg-amber-500 text-black hover:bg-amber-500 hover:text-black focus:bg-amber-500 focus:text-black",
                    day_today: "bg-gray-800 text-amber-400",
                    day_disabled: "text-gray-600 opacity-50",
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <label className="text-white text-sm font-medium mb-2 block">Hora preferida *</label>
            <Popover open={timeOpen} onOpenChange={setTimeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-gray-800 border-gray-700 text-amber-400 hover:bg-gray-700"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {formatTime(bookingData.time)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-2 bg-black border-gray-700" align="start">
                <div className="grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto">
                  {timeSlots.map((time) => (
                    <Button
                      key={time}
                      variant={bookingData.time === time ? "default" : "outline"}
                      className={`${bookedSlots.includes(time)
                        ? "bg-gray-900 border-gray-700 text-gray-600 cursor-not-allowed"
                        : bookingData.time === time
                          ? "bg-amber-500 text-black hover:bg-amber-600"
                          : "bg-gray-800 border-gray-700 text-amber-400 hover:bg-gray-700"
                        }`}
                      disabled={bookedSlots.includes(time)}
                      onClick={() => {
                        if (bookedSlots.includes(time)) return;
                        setBookingData({ ...bookingData, time });
                        setTimeOpen(false);
                      }}
                    >
                      {time}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <label className="text-white text-sm font-medium mb-2 block">Notas (opcional)</label>
            <Textarea
              value={bookingData.notes}
              onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
              className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              placeholder="Alguna preferencia especial..."
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleBooking}
              disabled={!bookingData.service || !bookingData.date || !bookingData.time || loading || bookingBarbershop?.accepting_bookings === false}
              className="flex-1 bg-amber-500 hover:bg-amber-700"
            >
              {loading ? 'Enviando...' : 'Enviar Reserva'}
            </Button>
            <Button
              onClick={() => {
                setShowBooking(false);
                setBookingData({ service: '', date: '', time: '', notes: '' });
                setDateOpen(false);
                setTimeOpen(false);
              }}
              variant="outline"
              className="flex-1 border-gray-700 text-white hover:bg-gray-800"
            >
              Cancelar
            </Button>
          </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
